(ns practitest.frontend.components2.entity-followers
  (:require [ajax.core :as ajax]
            [practitest.frontend.common.permissions :as permissions]
            [re-frame.core :as re-frame]
            [reagent.core :as reagent]
            [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.format-utils :as fu]
            [practitest.frontend.common.i18n :refer [get-translation
                                                     format-translation]]
            [practitest.frontend.common.mixpanel :as mixpanel]
            [practitest.frontend.common-entities.metadata :as metadata]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components.navigation :refer [user-avatar2]]
            [practitest.frontend.components.notifications :as notifications]
            [practitest.frontend.components.overlay2 :as overlay2]
            [practitest.frontend.components.tooltip :as tooltip]
            [practitest.frontend.routing.backend-routes :as broutes]
            [practitest.frontend.subs :as subs]
            [clojure.string :as str]))

(defn- filtered-users [all-users followers search-text]
  (if (empty? search-text)
    []
    (filter #(and (not (contains? (set (map :id followers)) (:id %)))
                  (fu/substring-search? (:name %) search-text)) all-users)))

(defn- toggle-current-view [user entity-type]
  [:div.form-switch.ms-auto
   [:input.form-check-input.clickable
    {:type      "checkbox"
     :checked   (boolean (:following? user))
     :on-change (fn []
                  (let [action-type (if (:following? user) :delete :create)]
                    (re-frame/dispatch [::update-follower (:id user) action-type entity-type])))}]])

(defn- remove-follower-view [user entity-type]
  [:span.remove-follower-icon.clickable
   {:on-click #(re-frame/dispatch [::update-follower (:id user) :delete entity-type])}
   (icons/mux-close)])

(defn- user-view [_ _ _]
  (let [hover? (reagent/atom false)]
    (fn [user action search-text entity-type]
      [:li.selector-item
       {:class (str (dom/classes-pairs :hover @hover?) " " (when (= action :add) "clickable"))
        :on-click #(when (= action :add)
                     (re-frame/dispatch [::update-follower (:id user) :create entity-type])
                     (reset! search-text ""))
        :on-mouse-over #(reset! hover? true)
        :on-mouse-out #(reset! hover? false)}
       [user-avatar2 user]
       [:span.selector-item-text
        (if (= action :toggle)
          (get-translation "followers.me" "Me")
          (:name user))]

       (if (= action :toggle)
         (toggle-current-view user entity-type)
         (when (and (= action :remove) @hover?)
           (remove-follower-view user entity-type)))])))

(defn- add-followers-view [input-focus? input-ref search-text filtered-users entity-type]
  [:<>
   [:hr.separator]
   [:div.add-textbox
    [:div.plus-icon
     {:class (when @input-focus? "hidden")
      :on-click (fn []
                  (reset! input-focus? true)
                  (when @input-ref (.focus @input-ref)))}
     [icons/mux-plus-circle]]
    [:input.mux-input.add-input
     {:class (when-not @input-focus? "has-icon")
      :ref (fn [node]
              (reset! input-ref node))
      :value @search-text
      :placeholder (get-translation "followers.add-users" "Add users to follow")
      :on-focus #(reset! input-focus? true)
      :on-blur #(reset! input-focus? false)
      :on-change (fn [e] (reset! search-text (dom/event-value e)))}]]
   (if (and (not-empty @search-text) (empty? filtered-users))
     [:div.no-results (get-translation "followers.no-results" "No results found")]
     (into [:ul.list-unstyled.followers-list]
           (for [user (sort-by :name filtered-users)]
             [user-view user :add search-text entity-type])))])

(defn entity-followers [_]
  (let [dropdown-opened? (reagent/atom false)
        input-focus? (reagent/atom false)
        search-text (reagent/atom "")
        input-ref (reagent/atom nil)]

    (fn [followers entity-type]
      (let [current-user @(re-frame/subscribe [::subs/current-user])
            all-users @(re-frame/subscribe [::subs/users])
            can-edit-others? (permissions/has-permission? "notification_list")
            other-followers (filter #(not (= (:id %) (:id current-user))) followers)
            filtered-users (filtered-users all-users followers @search-text)]
        [overlay2/floating-ui-overlay
         {:placement        "bottom-end"
          :offset           5
          :show?            @dropdown-opened?
          :on-request-close #(reset! dropdown-opened? false)}
         [tooltip/mux-tooltip
          {:position "bottom"}
          (get-translation "followers.tooltip" "Followers")
          (fn [props]
            [:div.followers-container
             (assoc props :on-click #(do
                                       (swap! dropdown-opened? not)
                                       (when @dropdown-opened?
                                         (mixpanel/track "Open Notification List"
                                                         {:Entity (get-in metadata/entities-metadata [entity-type :module])}))))
             [icons/mux-people]
             [:span (count followers)]
             (if @dropdown-opened?
               [icons/mux-chevron-up2]
               [icons/mux-chevron-down2])])]

         [:div.selector-dropdown.selector-items-container.followers-dropdown
          (into [:ul.list-unstyled.followers-list]
                (cons
                 [user-view (assoc current-user :following? (some #(= (:id %) (:id current-user)) followers)) :toggle nil entity-type]
                 (for [user (sort-by :name other-followers)]
                   [user-view user (if can-edit-others? :remove :view) nil entity-type])))
          (when can-edit-others?
            (add-followers-view input-focus? input-ref search-text filtered-users entity-type))]]))))

(re-frame/reg-event-fx
 ::update-follower
 (fn [{db :db} [_ follower-id action entity-type]]
   (let [entity-name (str/lower-case (metadata/get-entity-name entity-type :singular-name))]
     {:db db
      :http-xhrio
      {:method          (if (= action :create) :post :delete)
       :uri             (broutes/get-url (str "watchers#" (name action))
                                         {:id (subs/get-current-entity-id db entity-type)})
       :params          {:class_name (get-in metadata/entities-metadata [entity-type :db-entity])
                         :user_id follower-id
                         :id (subs/get-current-entity-id db entity-type)}
       :format          (ajax/json-request-format)
       :response-format (ajax/json-response-format {:keywords? true})
       :on-success      [::on-update-followers-success entity-type]
       :on-failure      [::notifications/error2 (get-translation (str entity-name ".notify.error-saving-followers")
                                                               (str "Error updating" entity-name "followers"))]}})))

(re-frame/reg-event-fx
 ::on-update-followers-success
 (fn [{db :db} [_ entity-type response]]
   {:db (assoc-in db [:entity entity-type :current :followers] (:followers response))}))